import { CardContainerContextProvider } from 'crm-cards-sdk/internal/CardContainerContext';
import { useLocationService } from '../internal/LocationServiceContext';
import MasonryCardFormatter from 'crm-cards-sdk/card-formatters/MasonryCardFormatter';
import { createContainerContentKey } from './createContainerContentKey';
import RGL, { WidthProvider } from 'react-grid-layout';
import { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { View } from 'foundations-components/components/View';
import { RowHeightPx, SmallerRowHeightPx, RowMarginPx, ColMarginPx, GridCols } from './MasonryLayoutConstants';
import trackUsage from '../utils/trackUsage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// Enhance the Responsive component with automatic width calculation
const ReactGridLayout = WidthProvider(RGL);

// Custom hook to consolidate ResizeObserver setup and lifecycle management
function useGridResizeObserver(handleResize, gridRef, layout) {
  const resizeObserverRef = useRef(null);

  // Initialize ResizeObserver with the resize handler
  useEffect(() => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }
    resizeObserverRef.current = new ResizeObserver(handleResize);
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [handleResize]);

  // Observe grid item content elements when layout changes
  useEffect(() => {
    if (gridRef.current && resizeObserverRef.current) {
      const contentElements = gridRef.current.querySelectorAll('.grid-item');
      contentElements.forEach(el => {
        const firstChild = el.firstElementChild;
        if (firstChild) {
          resizeObserverRef.current.observe(firstChild);
        }
      });
    }
  }, [layout, gridRef]);
}
const findCardInContents = (contents, cardId) => {
  return contents.find(content => content.type === 'CARD' && content.id === cardId);
};
const MasonryContainerFormatter = ({
  contents,
  containerId: containerIdProp,
  CrmContainerContentRenderer,
  topSlot,
  layout: containerLayout,
  observerManager,
  CardFormatter,
  usageTracker
}) => {
  var _containerLayout$enab, _containerLayout$rowH;
  const containerId = containerIdProp || 'NOCONTAINERID';
  const cardDraggingEnabled = (_containerLayout$enab = containerLayout === null || containerLayout === void 0 ? void 0 : containerLayout.enableDnd) !== null && _containerLayout$enab !== void 0 ? _containerLayout$enab : false;
  const {
    updateMasonryCardPositions
  } = useLocationService();
  const gridRef = useRef(null);
  const [sidecarRglState, setSidecarRglState] = useState(new Map());
  const configuredRowHeightPx = (_containerLayout$rowH = containerLayout === null || containerLayout === void 0 ? void 0 : containerLayout.rowHeightPx) !== null && _containerLayout$rowH !== void 0 ? _containerLayout$rowH : cardDraggingEnabled ? RowHeightPx : SmallerRowHeightPx;
  const previousLayoutRef = useRef(null);
  const CardCols = containerLayout && typeof containerLayout.columns === 'number' ? containerLayout.columns : 2;
  const CardColumnWidth = GridCols / CardCols;
  const setLayoutToCrmLocationStateCore = useCallback(newLayout => {
    previousLayoutRef.current = newLayout;
    const newSidecarRglState = new Map();
    const __positions = newLayout.map(item => {
      const itemsInSidebar = {
        // Everything except the x, y, and width stored on in the location
        i: item.i,
        h: item.h,
        minW: item.minW,
        maxW: item.maxW,
        minH: item.minH,
        maxH: item.maxH,
        moved: item.moved,
        static: item.static,
        isDraggable: item.isDraggable,
        isResizable: item.isResizable,
        resizeHandles: item.resizeHandles,
        isBounded: item.isBounded
      };
      newSidecarRglState.set(item.i, itemsInSidebar);
      return {
        x: item.x,
        y: item.y,
        containerContentKey: item.i,
        width: item.w / CardColumnWidth // Convert back to card width units
      };
    });
    setSidecarRglState(newSidecarRglState);

    // Update card positions using the location service
    if (updateMasonryCardPositions) {
      const masonryPositions = newLayout.filter(item => {
        // Only save positions for actual cards, not containers
        const cardId = extractCardIdFromKey(containerId, item.i);
        return cardId !== null;
      }).map(item => ({
        cardId: extractCardIdFromKey(containerId, item.i),
        x: item.x,
        y: item.y,
        width: item.w,
        height: item.h
      }));
      updateMasonryCardPositions(containerId, masonryPositions);
    }
  }, [CardColumnWidth, updateMasonryCardPositions, containerId]);
  const setLayoutToCrmLocationState = useCallback(callBackOrNewState => {
    if (typeof callBackOrNewState === 'function') {
      const newLayout = callBackOrNewState(previousLayoutRef.current || []);
      setLayoutToCrmLocationStateCore(newLayout);
    } else {
      setLayoutToCrmLocationStateCore(callBackOrNewState);
    }
  }, [setLayoutToCrmLocationStateCore]);
  const layoutFromLocation = useMemo(() => {
    return calculateLayoutStateFromCrmLocationState(containerId, contents, configuredRowHeightPx, CardCols, CardColumnWidth, sidecarRglState);
  }, [containerId, contents, configuredRowHeightPx, CardCols, CardColumnWidth, sidecarRglState]);
  const handleResize = useMemo(() => {
    return createDebouncedResizeHandler(setLayoutToCrmLocationState, gridRef, configuredRowHeightPx, previousLayoutRef);
  }, [configuredRowHeightPx, setLayoutToCrmLocationState]);

  // Use consolidated ResizeObserver hook
  useGridResizeObserver(handleResize, gridRef, layoutFromLocation);
  const handleLayoutChange = useCallback(changedLayout => {
    setLayoutToCrmLocationState(changedLayout);
  }, [setLayoutToCrmLocationState]);
  const handleDragStop = useCallback((__layout, oldItem, newItem) => {
    const cardId = extractCardIdFromKey(containerId, newItem.i);
    if (!cardId) {
      return;
    }
    const card = findCardInContents(contents, cardId);
    if (!card) {
      return;
    }
    const positionChanged = oldItem.x !== newItem.x || oldItem.y !== newItem.y;
    if (usageTracker) {
      trackUsage(usageTracker, 'Crm Cards', 'Masonry Container Interaction', {
        action: positionChanged ? 'cardMoved' : 'cardMoveCanceled',
        cardType: card.type
      });
    }
  }, [usageTracker, containerId, contents]);
  const handleResizeStop = useCallback((__layout, oldItem, newItem) => {
    const cardId = extractCardIdFromKey(containerId, newItem.i);
    if (!cardId) {
      return;
    }
    const card = findCardInContents(contents, cardId);
    if (!card) {
      return;
    }
    const sizeChange = getSizeChangeDescription(oldItem.w, oldItem.h, newItem.w, newItem.h);
    if (usageTracker) {
      trackUsage(usageTracker, 'Crm Cards', 'Masonry Container Interaction', {
        action: sizeChange ? 'cardResized' : 'cardResizeCanceled',
        cardType: card.type,
        sizeChange
      });
    }

    // Trigger resize recalculation when user manually resizes a grid item
    // This ensures content fits properly in the new container size
    handleResize();
  }, [handleResize, containerId, usageTracker, contents]);
  return /*#__PURE__*/_jsxs(CardContainerContextProvider, {
    value: {
      DefaultCardFormatter: CardFormatter || MasonryCardFormatter,
      reorderable: false,
      observerManager
    },
    children: [topSlot, /*#__PURE__*/_jsx(View, {
      as: "div",
      ref: gridRef,
      "data-card-container-layout": "masonry",
      children: /*#__PURE__*/_jsx(ReactGridLayout, {
        layout: layoutFromLocation,
        cols: GridCols,
        draggableHandle: '[data-masonry-card-drag-handle="true"]',
        rowHeight: configuredRowHeightPx,
        margin: [ColMarginPx, RowMarginPx],
        isResizable: cardDraggingEnabled,
        isDraggable: cardDraggingEnabled,
        onLayoutChange: handleLayoutChange,
        onDragStop: handleDragStop,
        onResizeStop: handleResizeStop,
        compactType: 'vertical'
        // Let the cards follow each other during height adjustments
        ,
        useCSSTransforms: false
        // Don't measure before mount to allow initial card content to determine size
        ,
        measureBeforeMount: false,
        children: contents.map(content => {
          const key = createContainerContentKey(containerId, content);
          const id = content.type === 'CARD' ? `grid-card-wrapper-${content.id}` : `grid-container-wrapper-${content.id}`;
          return (
            /*#__PURE__*/
            // @bend-expect trellis-tokenized
            _jsx("div", {
              className: "grid-item",
              "data-key": key,
              id: id,
              children: /*#__PURE__*/_jsx("div", {
                style: {
                  overflow: 'auto'
                },
                children: /*#__PURE__*/_jsx(CrmContainerContentRenderer, {
                  content: content
                })
              })
            }, key)
          );
        })
      })
    })]
  });
};
function debounce(fn, delay) {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}
function createResizeHandler(setLayout, gridRef, rowHeight, previousLayoutRef) {
  return (force = false) => {
    // Use a function with setLayout to get the latest layout state
    // This avoids the need for layout in the dependency array
    setLayout(currentLayout => {
      var _gridRef$current;
      let hasChanges = force;
      const newLayout = [...currentLayout];
      const gridItems = (_gridRef$current = gridRef.current) === null || _gridRef$current === void 0 ? void 0 : _gridRef$current.querySelectorAll('.grid-item');
      Array.from(gridItems || []).forEach(gridItem => {
        const cardElement = gridItem.firstElementChild;
        const cardKey = gridItem.getAttribute('data-key');
        if (cardElement && cardKey) {
          const cardHeight = cardElement.clientHeight;
          const newRowSpan = Math.max(1, Math.ceil((cardHeight + RowMarginPx) / (rowHeight + RowMarginPx)));

          // Find the layout item by key
          const layoutItemIndex = newLayout.findIndex(item => item.i === cardKey);
          if (layoutItemIndex !== -1 && newLayout[layoutItemIndex].h !== newRowSpan) {
            newLayout[layoutItemIndex] = Object.assign({}, newLayout[layoutItemIndex], {
              h: newRowSpan
            });
            hasChanges = true;
          }
        }
      });

      // Return new layout only if changes were made
      if (hasChanges) {
        previousLayoutRef.current = newLayout;
        return newLayout;
      }
      return currentLayout;
    });
  };
}
function createDebouncedResizeHandler(setLayout, gridRef, rowHeight, previousLayoutRef) {
  const resizeHandler = createResizeHandler(setLayout, gridRef, rowHeight, previousLayoutRef);
  return debounce(resizeHandler, 100);
}
function calculateLayoutStateFromCrmLocationState(containerId, contents, rowHeight, columns, CardColumnWidth, sidecarRglState) {
  return contents.filter(content => content.type === 'CARD').map((content, __i) => {
    if (content.type !== 'CARD') {
      return null;
    }
    const key = createContainerContentKey(containerId, content);
    const previousLayout = content.layout;
    const sidecarRglLayout = sidecarRglState.get(key);
    const layout = Object.assign({
      i: key,
      // Defaults
      minW: 1,
      minH: 1,
      h: 2
    }, sidecarRglLayout, {
      x: previousLayout.x || 0,
      y: previousLayout.y || 0,
      w: previousLayout.width ? previousLayout.width * CardColumnWidth : CardColumnWidth
    });
    return layout;
  }).filter(x => x != null);
}
function extractCardIdFromKey(containerId, layoutItemKey) {
  // Format: container:masonry-container-1:contentId:CARD:company-summary-card-1:
  const expectedPrefix = `container:${containerId}:contentId:CARD:`;
  if (layoutItemKey.startsWith(expectedPrefix) && layoutItemKey.endsWith(':')) {
    const cardId = layoutItemKey.substring(expectedPrefix.length, layoutItemKey.length - 1);
    return cardId;
  }
  return null;
}
function getSizeChangeDescription(oldWidth, oldHeight, newWidth, newHeight) {
  const widthChanged = oldWidth !== newWidth;
  const heightChanged = oldHeight !== newHeight;
  if (widthChanged && heightChanged) {
    const widthIncreased = newWidth > oldWidth;
    const heightIncreased = newHeight > oldHeight;
    if (widthIncreased && heightIncreased) {
      return 'increasedWidthAndHeight';
    } else if (widthIncreased && !heightIncreased) {
      return 'increasedWidthDecreasedHeight';
    } else if (!widthIncreased && heightIncreased) {
      return 'decreasedWidthIncreasedHeight';
    } else {
      return 'decreasedWidthAndHeight';
    }
  } else if (widthChanged) {
    return newWidth > oldWidth ? 'increasedWidth' : 'decreasedWidth';
  } else if (heightChanged) {
    return newHeight > oldHeight ? 'increasedHeight' : 'decreasedHeight';
  }
  return undefined;
}
export default MasonryContainerFormatter;